import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import Icon from '~/components/Icon/Icon';
import NewExperimentModal from '~/components/NewExperimentModal/NewExperimentModal';
import ChannelAdd from '~/components/channels/ChannelAdd/ChannelAdd';
import ChannelEdit from '~/components/channels/ChannelEdit/ChannelEdit';
import EditorNumber from '~/components/editor/EditorNumber/EditorNumber';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import Collapsible from '~/components/widgets/Collapsible/Collapsible';
import { useDevicesSpecs } from '~/hooks/useDevices';
import { useExportModalActions } from '~/hooks/useExportModalActions';
import { isDeviceConfigurable } from '~/models/device';
import { useExperiment } from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import { assert, getAtomsMagExteremes } from '~/util';
import type { Channel, Device } from '~/models/studio';
import type { ExperimentTx } from '~/systems/experiment';
import './DeviceControl.css';

interface DeviceControlProps {
  initialState?: boolean;
}

export default function DeviceControl({ initialState }: DeviceControlProps) {
  const uiState = useUiState();
  const {
    clear,
    channels,
    commitedAtoms: atoms,
    computedPulses: pulses,
    device,
    openTransaction
  } = useExperiment();
  const { data: devicesData } = useDevicesSpecs();
  const [selectedDeviceName, setSelectedDeviceName] = useState('');

  const {
    openConfirmOverwriteModal,
    handleCancelOverwrite,
    handleConfirmOverwrite,
    isConfirmOverwriteModalVisible
  } = useExportModalActions();

  const txRef = useRef<ExperimentTx | null>(null);
  const cmdIdxRef = useRef(0);

  function getEditTx() {
    if (txRef.current == null || !txRef.current.isOpen()) {
      txRef.current = openTransaction('deviceEdit');
      assert(device != null);
      cmdIdxRef.current = txRef.current.push({
        kind: 'update_device',
        props: {}
      });
    }
    return txRef.current;
  }

  function updateDevice(
    properties: Partial<Device>,
    commitImmediately = false
  ) {
    getEditTx().update(cmdIdxRef.current, (cmd) => {
      assert(cmd.kind === 'update_device');
      if (device == null) {
        return cmd;
      }
      return { ...cmd, props: properties };
    });
    if (commitImmediately) {
      commit();
    }
  }

  function commit() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.commit();
    }
  }

  useEffect(() => () => commit(), []);

  function setDevice(newDevice: Device, newChannels: Channel[]) {
    const tx = openTransaction('Set Device');
    tx.push({ kind: 'set_device', device: newDevice, channels: newChannels });
    tx.commit();
    uiState.selectChannel(null);
  }

  function onModelChange(val: string) {
    if (val === device.name) {
      return;
    }
    const devicesSpecs = devicesData?.devicesSpecs ?? {};
    setSelectedDeviceName(val);
    const selectedVal = devicesSpecs?.[val.toLowerCase()];

    if (val === 'MockDevice') {
      setDevice({ ...device, name: 'MockDevice' }, [...channels]);
      return;
    } else if (selectedVal) {
      const newDevice = selectedVal.device;
      const newChannels = selectedVal.channels.map(
        (channel: Partial<Channel>, idx: number) => ({
          label: `ch${idx}`,
          uuid: (channel as Channel).uuid || uuid(),
          ...channel
        })
      ) as Channel[];

      /**
       * To prevent error when changing from MockDevice to any other devices,
       * we clear the experiment if there is some Pulses or Atoms
       */
      if (pulses.length === 0 && atoms.length === 0) {
        setDevice(newDevice, newChannels);
      } else {
        openConfirmOverwriteModal(() => {
          clear();
          setDevice(newDevice, newChannels);
        });
      }
    }
  }

  const deviceOptions = useMemo(
    () =>
      ['MockDevice', ...(devicesData?.deviceNames ?? [])].map((option) => ({
        label: option,
        value: option
      })),
    [devicesData]
  );

  const limits = useMemo(() => {
    const deviceLimits = {
      maxAtomNum: { max: 0 as number, min: 0 },
      minAtomDistance: { max: 0 as number, min: 0 },
      maxRadialDistance: { min: 0, max: 0 as number },
      rydbergLevel: { min: 50, max: 100 }
    };
    if (isDeviceConfigurable(device)) {
      const atomMagExtremes = getAtomsMagExteremes(atoms.map((a) => a.pos));
      deviceLimits.maxAtomNum = {
        min: atoms.length,
        max: Infinity
      };
      deviceLimits.minAtomDistance = {
        max: Math.min(
          atomMagExtremes.shortestMag || Infinity,
          device.maxRadialDistance
        ),
        min: 0.001
      };
      deviceLimits.maxRadialDistance = {
        min: Math.max(atomMagExtremes.largestMag, device.minAtomDistance),
        max: Infinity
      };
    }
    return deviceLimits;
  }, [atoms, device]);

  const deviceCollapsibleRef = useRef<{ open(): void; close(): void }>(null);
  useEffect(() => {
    if (uiState.selectedChannel != null) {
      deviceCollapsibleRef.current?.open();
    }
  }, [uiState.selectedChannel]);

  const isFixedDevice = !isDeviceConfigurable(device);

  if (!device) {
    return null;
  }

  return (
    <>
      <div className="DeviceControl">
        <Collapsible
          ref={deviceCollapsibleRef}
          initialState={initialState}
          button={() => (
            <h3 className="DeviceControl-sectionTitle">
              <Icon kind="unity" /> Device specifications
            </h3>
          )}
          items={
            <>
              <div className="DeviceControl-sectionBody">
                <EditorSelect
                  label="Device"
                  name="device"
                  value={device.name}
                  onChange={onModelChange}
                  className="DeviceControl-deviceType"
                  options={deviceOptions}
                  disabled={
                    deviceOptions.length === 1 &&
                    deviceOptions[0].value === device.name
                  }
                />
                <EditorNumber
                  label="Dimensions"
                  name="dimensions"
                  value={device.dimensions}
                  max={3}
                  min={2}
                  step={1}
                  unit="D"
                  disabled
                  onChange={(v) => updateDevice({ dimensions: v })}
                  onBlur={commit}
                />
                <EditorNumber
                  label="Rydberg Level"
                  name="rydberg-level"
                  value={device.rydbergLevel}
                  max={limits.rydbergLevel.max}
                  min={limits.rydbergLevel.min}
                  step={1}
                  disabled={isFixedDevice}
                  onChange={(v) => updateDevice({ rydbergLevel: v })}
                  onBlur={commit}
                />
                <EditorNumber
                  label="Max Atom Number"
                  name="max-atom-number"
                  value={device.maxAtomNum}
                  max={limits.maxAtomNum.max}
                  min={limits.maxAtomNum.min}
                  step={1}
                  disabled={isFixedDevice}
                  onChange={(v) => updateDevice({ maxAtomNum: v })}
                  onBlur={commit}
                />
                <EditorNumber
                  label="Min Atom Distance"
                  name="min-atom-distance"
                  value={device.minAtomDistance}
                  max={limits.minAtomDistance.max}
                  min={limits.minAtomDistance.min}
                  step={0.001}
                  disabled={isFixedDevice}
                  unit="μm"
                  onChange={(v) => updateDevice({ minAtomDistance: v })}
                  onBlur={commit}
                />
                <EditorNumber
                  label="Max Radial Distance"
                  name="max-radial-distance"
                  value={device.maxRadialDistance}
                  max={limits.maxRadialDistance.max}
                  min={limits.maxRadialDistance.min}
                  step={0.001}
                  disabled={isFixedDevice}
                  unit="μm"
                  onChange={(v) => updateDevice({ maxRadialDistance: v })}
                  onBlur={commit}
                />
              </div>
              <div className="DeviceControl-availableChannels">
                <h4 className="DeviceControl-channelsHeader">
                  Available Channels
                </h4>
                <div className="DeviceControl-availableChannelsInner">
                  {channels.map((channel) => (
                    <ChannelEdit key={channel.uuid} channel={channel} />
                  ))}
                  <ChannelAdd />
                </div>
              </div>
            </>
          }
        />
      </div>
      <NewExperimentModal
        isModalVisible={isConfirmOverwriteModalVisible}
        onHandleContinue={() => handleConfirmOverwrite(true)}
        onHandleContinueWithoutSaving={() => handleConfirmOverwrite(false)}
        onClose={handleCancelOverwrite}
      >
        {device.name} specifications are not the same as {selectedDeviceName} so
        the experiment must be reset.
      </NewExperimentModal>
    </>
  );
}
